// core
import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { Event, RouterEvent, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

// services
import { IntercomService, LoggerService } from '@campfire-nx/api';
import { PartnerService } from '../resources/partner.service';
import { BrandingService } from '../resources/branding.service';

interface dataKeys {
  [key: string]: string | number | undefined;
}

interface partnerData extends dataKeys {
  slug?: string;
  brand?: any;
  name?: string;
  legalName?: string;
  redirect?: string;
  redirectBack?: string;
  productChannel?: string;
  marketingOffers?: any;
  companyInfo?: any;
  dashboardSidebar?: any;
  gtmContainerId?: any;
  disclaimer?: any;
  product?: any;
  intercom?: any;
}
interface brandData extends dataKeys {
  slug?: string;
  brand?: any;
}

interface data {
  [index: string]: any;
  partner: partnerData;
  brand: brandData;
}

@Injectable({
  providedIn: 'root'
})
export class PartnerBrandService {
  private renderer: Renderer2;
  data: data = {
    partner: {},
    brand: {}
  };

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private partnerService: PartnerService,
    private brandingService: BrandingService,
    rendererFactory: RendererFactory2,
    private intercomService: IntercomService,
    private loggerService: LoggerService
  ) {
    
    this.router.events
      .pipe(filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationEnd))
      .subscribe((e: RouterEvent) => {
        this.getPartner();
        this.getBrand();
      });
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  getAttribute(attribute: string, location: string) {
    const storedData = window.localStorage.getItem(location);
    const dataObject: any = storedData ? JSON.parse(storedData) : {};
    return this.data[location][attribute] || dataObject[attribute];
  }

  setAttribute(attribute: string, value: any, location: string) {
    const storedData = window.localStorage.getItem(location);
    const dataObject: any = storedData ? JSON.parse(storedData) : {};

    dataObject[attribute] = value;
    window.localStorage.setItem(location, JSON.stringify(dataObject));

    this.data[location][attribute] = value;
  }
  removeAttribute(attribute: string, location: string) {
    const storedData = window.localStorage.getItem(location);
    const dataObject: any = storedData ? JSON.parse(storedData) : {};

    delete dataObject[attribute];
    window.localStorage.setItem(location, JSON.stringify(dataObject));

    delete this.data[location][attribute];
  }

  setBrand(slug: string, brand?: any) {
    this.resetBrandData();
    this.setAttribute('slug', slug, 'brand');

    if (brand) {
      this.setAttribute('brand', brand, 'brand');
      return;
    }

    if (slug === 'open') {
      this.setAttribute('brand', {
        label: 'Open',
        slug: 'open'
      }, 'brand');
    } else {
      this.brandingService.brand(slug)
        .then((response: any) => {
          this.setAttribute('brand', response.portal_branding, 'brand');
        })
        .catch((error: any) => {
        });
    }
  }

  getBrand() {
    const storedData = window.localStorage.getItem('brand');
    this.data.brand = storedData ? JSON.parse(storedData) : {};
    return this.data.brand;
  }

  resetBrandData() {
    this.data.brand = {};
    window.localStorage.removeItem('brand');
  }

  setPartner(slug: string, saveBrand = true) {
    this.resetPartnerData();
    this.setAttribute('slug', slug, 'partner');
    this.applyBrand();

    if (slug === 'open') {
      this.setAttribute('brand', {
        label: 'Open',
        slug: 'open'
      }, 'partner');
    } else {
      this.partnerService.brand(slug)
        .then((response: any) => {
          this.setAttribute('brand', response.brand, 'partner');
          this.setAttribute('name', response.name, 'partner');
          this.setAttribute('legalName', response.legal_name, 'partner');
          this.setAttribute('cocEnabled', response.coc_enabled, 'partner');
          this.setAttribute('disclaimer', response.disclaimer, 'partner');
          this.setAttribute('isAppointmentEnabled', response.is_appointment_enabled, 'partner');
          this.setAttribute('isAuth0Enabled', response.is_auth0_enabled, 'partner');
          this.setAttribute('auth0ClientId', response.auth0_client_id, 'partner');
          this.setAttribute('intercom', response.intercom, 'partner');
          
          if (saveBrand) {
            this.setAttribute('slug', slug, 'brand');
            this.setAttribute('brand', response.brand, 'brand');
          }

          if (response.partner && response.partner.referrer) {
            const referrer = {
              slug: response.partner.referrer,
              is_referred: response.partner.is_referrer
            };
            this.setAttribute('referrer', referrer, 'partner');
          }

          this.setCompanyInfo(response.company_info);
          this.addPartnerScripts();
        })
        .catch((error: any) => {
        });
    }
  }

  getPartner() {
    const storedData = window.localStorage.getItem('partner');
    this.data.partner = storedData ? JSON.parse(storedData) : {};

    if (this.data.partner) {
      this.applyBrand(); 
    }
    return this.data.partner;
  }

  resetPartnerData() {
    this.data.partner = {};
    window.localStorage.removeItem('partner');
  }

  addPartnerScripts() {
    const product = this.getAttribute('product', 'partner');
    const gtmContainerID = this.getAttribute('gtmContainerId', 'partner');
    const gtmScript = this.document.getElementById('ga-script');
    const gtmIframe = this.document.getElementById('gtm-iframe');

    // Load GTM tag
    if (gtmContainerID && !gtmScript && !gtmIframe) {
      const ga = this.renderer.createElement('script');
      ga.id = 'ga-script';
      ga.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmContainerID}');`;
      this.renderer.appendChild(this.document.head, ga); // Insert in head as per spec

      // Add noscript as backup
      const gtm = this.renderer.createElement('noscript');
      const gtmIframe = this.renderer.createElement('iframe');
      gtmIframe.id = 'gtag-iframe';
      gtmIframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmContainerID}`;
      gtmIframe.height = "0";
      gtmIframe.width = "0";
      gtmIframe.style = "display:none;visibility:hidden";
      this.renderer.appendChild(gtm, gtmIframe);
      this.renderer.appendChild(this.document.body, gtm); // Insert in body as per spec
    }

    // Load Intercom script
    this.intercomService.addIntercomScripts(this.getAttribute('intercom', 'partner'));

    if (product && product.cash_rewards) {
      const cashrewards = this.renderer.createElement('script');
      cashrewards.id = 'cashrewards';
      cashrewards.text = `(function(a,b,c,d,e,f,g){e['ire_o']=c;e[c]=e[c]||function(){(e[c].a=e[c].a||[]).push(arguments)};f=d.createElement(b);g=d.getElementsByTagName(b)[0];f.async=1;f.src=a;g.parentNode.insertBefore(f,g);})('https://utt.impactcdn.com/A5666772-8e4a-4269-a39c-d605acd8dff51.js','script','ire',document,window); ire('identify');`;
      this.renderer.appendChild(this.document.body, cashrewards);
    }
  }

  applyBrand() {
    if (this.data.partner.slug) {
      const bodyClasses = this.document.body.classList;
      for (const bodyClass in bodyClasses) {
        if (
          bodyClasses.hasOwnProperty(bodyClass) &&
          bodyClasses[bodyClass].indexOf('brand-') > -1
        ) {
          this.document.body.classList.remove(bodyClasses[bodyClass]);
        }
      }
      this.document.body.classList.add(`brand-${  this.data.partner.slug}`);
    }
  }

  setFlowBranding(response:any) {
    // Get the provided and currently stored partner slugs for comparison
    // Also get the provided and currently stored branding slugs
    let newPartnerSlug = '';
    if (response && response.partner && response.partner.slug) {
      newPartnerSlug = response.partner.slug;
    }
    const newBrandingSlug = response?.brand;
    const existingBrandingSlug = this.getAttribute('slug', 'brand');
    const existingPartnerSlug = this.getAttribute('slug', 'partner');
    const existingProduct = this.getAttribute('product', 'partner');
    const newProduct = response?.product; 

    // If a new branding slug has been provided, use that for branding.
    if (newBrandingSlug !== existingBrandingSlug && newBrandingSlug) {
      this.setBrand(newBrandingSlug);
    }
    
    // If the partner slug is different, or the product is different, update the handover data
    if (
      (!existingProduct && newProduct) || 
      (newPartnerSlug !== existingPartnerSlug && newPartnerSlug !== '')
    ) {
      this.setHandoverData(response);
    } else if (newPartnerSlug === '') {
      this.loggerService.warn('PartnerBrandService', 'setFlowBranding', { warn: response?.analytics }, 'newPartnerSlug is empty');
    } else if (!newBrandingSlug) {
      this.setBrand(existingPartnerSlug, this.getAttribute('brand', 'partner'));
    }
  }

  setCompanyInfo(companyInfo: any) {
    const info = companyInfo;
    if (companyInfo) {
      // Create compact number for links
      if (companyInfo.SUPPORT_PHONE) {
        info.SUPPORT_PHONE_COMPACT = companyInfo.SUPPORT_PHONE.split(' ').join('');
      }
      if (companyInfo.SUPPORT_PHONE_INTL) {
        info.SUPPORT_PHONE_INTL_COMPACT = companyInfo.SUPPORT_PHONE_INTL.split(' ').join('');
      }

      // Add protocol to terms and privacy urls
      info.TERMS_URL = `https://${  companyInfo.TERMS_URL}`;
      info.PRIVACY_URL = `https://${  companyInfo.PRIVACY_URL}`;

      this.setAttribute('companyInfo', info, 'partner');
    }
  }

  setHandoverData(response: any) {
    const partnerSlug = response?.partner?.slug;
    const brandingSlug = response?.brand;

    if (partnerSlug && typeof brandingSlug === 'undefined') {
      this.setPartner(partnerSlug);
    } else if (partnerSlug) {
      this.setPartner(partnerSlug, false);
      this.setBrand(brandingSlug);
    } else {
      this.setPartner('open');
      this.loggerService.warn('PartnerBrandService', 'setHandoverData', { warn: response?.analytics }, 'partnerSlug does not exist');
    }

    if (response.product) {
      this.setAttribute('product', response.product, 'partner');
    }
    if (response.redirect) {
      this.setAttribute('redirect', response.redirect, 'partner');
    }
    if (response.redirectBack) {
      this.setAttribute('redirectBack', response.redirectBack, 'partner');
    }
    if (response.product && response.product.channel) {
      this.setAttribute('productChannel', response.product.channel, 'partner');
    }
    if (response.marketing_options) {
      this.setAttribute('marketingOffers', response.marketing_options, 'partner');
    }
    if (response.partner && response.partner.company_info) {
      this.setCompanyInfo(response.partner.company_info);
    }
    if (response.partner && response.partner.portal_dashboard_sidebar) {
      this.setAttribute('dashboardSidebar', response.partner.portal_dashboard_sidebar, 'partner');
    }
    if (response.partner && response.partner.gtm_container_id) {
      this.setAttribute('gtmContainerId', response.partner.gtm_container_id, 'partner');
    }
    if (response.referrer) {
      this.setAttribute('referrer', response.referrer, 'partner');
      this.setAttribute('isReferred', response.is_referred, 'partner');
    }
    if (response.disclaimer) {
      this.setAttribute('disclaimer', response.disclaimer, 'partner');
    }

    this.applyBrand();
  }

  getCurrency() {
    const product = this.getAttribute('product', 'partner');

    if (product?.country?.currency) {
      return product.country.currency;
    } else {
      return 'AUD'; // default currency
    }
  }

  getCurrencySymbol() {
    const product = this.getAttribute('product', 'partner');

    if (product?.country?.currency_symbol) {
      return product.country.currency_symbol;
    } else {
      return '$'; // default currency symbol
    }
  }

  getCountryCode(countryCodeFormat: string): string {
    const product = this.getAttribute('product', 'partner');
    const defaultCountryCode = 'AU';

    if (product && product.country) {
      if (product.country[countryCodeFormat]) {
        return product.country[countryCodeFormat];
      } else {
        return defaultCountryCode;
      }
    } else {
      return defaultCountryCode;
    }

  }

}
